import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Hero from '@/components/Hero';
import FilterBar from '@/components/FilterBar';
import RecipientCard from '@/components/RecipientCard';
import { api } from '@/lib/api';
import { Loader2 } from 'lucide-react';

export default function Home() {
    const [recipients, setRecipients] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('');
    const [sort, setSort] = useState('milestones');

    // Fetch stats once (and again after any recipient list changes)
    useEffect(() => {
        api.get('/stats').then(({ data }) => setStats(data)).catch(() => {});
    }, [recipients.length]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get('/recipients', { params: { q: query, category, sort } })
            .then(({ data }) => { if (!cancelled) setRecipients(data); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [query, category, sort]);

    const total = recipients.length;

    return (
        <div className="App">
            <Navbar variant="public" />
            <Hero stats={stats} />

            {/* Directory: every recipient, no featuring */}
            <section
                id="recipients"
                data-testid="recipients-section"
                className="bg-black"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
                    <div className="mb-8 md:mb-10">
                        <div className="text-xs uppercase tracking-[0.28em] text-un-lime font-semibold">
                            Award Recipients
                        </div>
                        <h2 className="mt-2 font-display text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                            UnCloned<span className="text-un-berry">®</span> Brand Award Awardees
                        </h2>
                        <p className="mt-3 text-white/60 max-w-2xl font-body">
                            Search, filter by phase, or sort to explore every UnCloned<span className="text-un-berry">®</span> Brand Award recipient.
                        </p>
                    </div>

                    <FilterBar
                        query={query} onQueryChange={setQuery}
                        category={category} onCategoryChange={setCategory}
                        sort={sort} onSortChange={setSort}
                        total={total}
                    />

                    <div className="mt-10">
                        {loading ? (
                            <div className="flex items-center justify-center py-24 text-white/60">
                                <Loader2 className="animate-spin" size={22} />
                                <span className="ml-3 uppercase tracking-[0.2em] text-xs">Loading</span>
                            </div>
                        ) : recipients.length === 0 ? (
                            <div
                                data-testid="empty-state"
                                className="border border-dashed border-white/15 rounded-3xl p-12 text-center text-white/60"
                            >
                                <div className="font-display text-2xl text-white mb-2">No matches</div>
                                <div className="text-sm">Try clearing your search or picking a different category.</div>
                            </div>
                        ) : (
                            <div
                                data-testid="recipients-grid"
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
                            >
                                {recipients.map((r, i) => (
                                    <RecipientCard key={r.id} recipient={r} index={i} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
